import React, { useEffect, useState } from 'react';
import { GetReportInput, GetReportOutput, Report } from 'aws-sdk/clients/d3aservice';
import { getReport } from 'src/api/d3a';
import { ReportDetail } from 'src/components/auditReports/reportDetail';
import { Button, Header, Modal, SpaceBetween, Spinner, TextContent } from '@amzn/awsui-components-react-v3';
import { DEFAULT_CONTENT_TYPE, REPORT_TYPE_D3A_DIFF } from 'src/commons/constants';
import { Link } from 'src/components/common/Link';
import { CommonPageProps } from 'src/components/common/CommonPageProps';

export interface ReportDetailPageProps extends CommonPageProps {}

export const REPORT_READY_STATUS = 'Ready';

export const ReportDetailPage = (props: ReportDetailPageProps) => {
  const [report, setReport] = useState<Report>(undefined);
  const [s3Urls, setS3Urls] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [downloadModalVisible, setDownloadModalVisible] = useState<boolean>(false);
  const [sourceReport1, setSourceReport1] = useState<Report>(undefined);
  const [sourceReport2, setSourceReport2] = useState<Report>(undefined);

  const reportId = () => {
    return props.match.params.id;
  };

  const handleRefresh = async () => {
    setLoading(true);
    props.setContentType(DEFAULT_CONTENT_TYPE);
    try {
      const getReportInput: GetReportInput = {
        reportId: reportId(),
      };

      const getReportOutput: GetReportOutput = await getReport(getReportInput);

      setReport(getReportOutput.report);
      setS3Urls(Object.values(getReportOutput.d3aProcessedS3UrlsMap));

      // If this is a diff report, fetch the source reports
      if (getReportOutput.report.type === REPORT_TYPE_D3A_DIFF && getReportOutput.report.diffReportConfig) {
        const [report1Output, report2Output] = await Promise.all([
          getReport({ reportId: getReportOutput.report.diffReportConfig.reportId1 }),
          getReport({ reportId: getReportOutput.report.diffReportConfig.reportId2 }),
        ]);
        setSourceReport1(report1Output.report);
        setSourceReport2(report2Output.report);
      }
    } catch (e) {}
    setLoading(false);
  };

  const handleDownload = () => {
    setDownloadModalVisible(true);
  };

  const handleDownloadAll = () => {
    s3Urls?.forEach((url, index) => {
      setTimeout(() => window.open(url, '_blank'), index * 1000);
    });
    setDownloadModalVisible(false);
  };

  useEffect(() => {
    handleRefresh();
  }, [reportId()]); // Re-run when reportId changes

  const fileNameForPresignedUrl = (url: string) => {
    const fileName = url.split('/').pop()?.split('?')[0];
    return fileName || 'Unknown';
  };

  if (loading) return <Spinner />;

  return (
    <>
      <Modal
        visible={downloadModalVisible}
        header={<Header variant={'h2'}>Download report</Header>}
        onDismiss={() => setDownloadModalVisible(false)}
        footer={
          <Header
            variant={'h2'}
            actions={
              <SpaceBetween size={'xs'} direction={'horizontal'}>
                <Button onClick={() => setDownloadModalVisible(false)}>Cancel</Button>
                <Button variant={'primary'} onClick={handleDownloadAll}>
                  Download all
                </Button>
              </SpaceBetween>
            }
          ></Header>
        }
      >
        <TextContent>
          <p>Select a link to download the associated file from S3, or select "Download all."</p>
          <ul>
            {s3Urls.map((url, index) => (
              <li key={index}>
                <Link href={url} external={true}>
                  {fileNameForPresignedUrl(url)}
                </Link>
              </li>
            ))}
          </ul>
        </TextContent>
      </Modal>
      <SpaceBetween size={'l'} direction={'vertical'}>
        <Header
          variant={'h1'}
          description={'Report ID: ' + report.reportId}
          actions={
            <SpaceBetween size={'xs'} direction={'horizontal'}>
              <Button variant={'icon'} iconName={'refresh'} onClick={handleRefresh} />
              <Button
                variant={'primary'}
                disabled={loading || report?.status != REPORT_READY_STATUS}
                onClick={handleDownload}
              >
                Download
              </Button>
            </SpaceBetween>
          }
        >
          {report.name}
        </Header>
        <ReportDetail
          setContentType={props.setContentType}
          activeGroup={props.activeGroup}
          report={report}
          s3Urls={s3Urls}
          sourceReport1={sourceReport1}
          sourceReport2={sourceReport2}
        />
      </SpaceBetween>
    </>
  );
};
